/**
 * The typed API client, and the one place an error becomes something a
 * component can render.
 *
 * Every route answers `{ ok: true, ... }` or `{ ok: false, reason }`, so this
 * has one job: turn the second into a thrown `ApiError` carrying the sentence
 * the worker wrote, and return the first with its own type. No component ever
 * reads a status code to choose words.
 */

import {
  ORG_PATHS,
  ROUTES,
  orgPath,
  type AccountStatus,
  type BillingRedirect,
  type BillingView,
  type SignupResponse,
  type ActRequest,
  type CancelRequest,
  type AdminOverview,
  type AuditExportResponse,
  type AuditQuery,
  type AuditResponse,
  type AuditSearchResponse,
  type DataExport,
  type DepartmentsResponse,
  type PeopleBulkResponse,
  type TailorRequest,
  type TailorResponse,
  type DataExportResponse,
  type BoardResponse,
  type EmployeeResponse,
  type EmployeesResponse,
  type InstancesResponse,
  type WeekResponse,
  type LevelCreateRequest,
  type LevelSaveRequest,
  type MailEvery,
  type MailEveryResponse,
  type MailWhat,
  type MailWhatResponse,
  type MoveRequest,
  type HealthReport,
  type MeResponse,
  type NoticesResponse,
  type OverdueResponse,
  type OrganizationRenameResponse,
  type PeopleResponse,
  type PersonAddRequest,
  type PersonUpdateRequest,
  type PersonUpdateResponse,
  type AdminOrganizationView,
  type MailRequestCreate,
  type MailUsage,
  type QueueResponse,
  type SetupResponse,
  type StaffingRequest,
  type CheckpointRequest,
  type CohortRequest,
  type CohortResponse,
  type StartRequest,
  type StartResponse,
  type MigrateRequest,
  type MigrationResponse,
  type Template,
  type TemplateImportResponse,
  type TemplateResponse,
  type TemplatesResponse,
} from "@utuwrk/core";

export class ApiError extends Error {
  readonly status: number;
  /** Named bindings or secrets, when the worker says it is not configured. */
  readonly missing: string[] | undefined;

  constructor(reason: string, status: number, missing?: string[]) {
    super(reason);
    this.name = "ApiError";
    this.status = status;
    this.missing = missing;
  }
}

/** What a failure that never reached the worker says: offline, or `dev:web` with no `wrangler dev` behind it. */
export const UNREACHABLE = "utuwrk could not be reached. Check your connection and try again.";

/**
 * What a request that never answered says. Without a limit, a worker or an
 * organisation's object that stopped answering left every screen on
 * "Loading…" for good, with nothing to read and nothing to retry (found in
 * the first full run-through). A write that times out may still have been
 * saved, which is why this says to look rather than to do it again.
 */
export const TOO_SLOW = "utuwrk took too long to answer. Reload in a moment to see where things stand.";

/** Long enough for a write that mails several people; far shorter than anybody waits. */
export const REQUEST_TIMEOUT_MS = 20_000;

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const timeout = AbortSignal.timeout(REQUEST_TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(path, {
      // Same origin, so the session cookie rides along with nothing else configured.
      credentials: "same-origin",
      signal: timeout,
      ...init,
      headers: {
        Accept: "application/json",
        ...(init.body ? { "Content-Type": "application/json" } : {}),
        ...init.headers,
      },
    });
  } catch {
    throw new ApiError(timeout.aborted ? TOO_SLOW : UNREACHABLE, 0);
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    if (timeout.aborted) throw new ApiError(TOO_SLOW, response.status);
    // A proxy's error page, or the single-page fallback answering an /api path.
    throw new ApiError(UNREACHABLE, response.status);
  }

  const failed = body as { ok?: unknown; reason?: unknown; missing?: string[] } | null;
  if (!response.ok || !failed || failed.ok !== true) {
    throw new ApiError(
      typeof failed?.reason === "string" ? failed.reason : "Something went wrong. Try again.",
      response.status,
      failed?.missing,
    );
  }
  return body as T;
}

const post = <T>(path: string, body: unknown = {}): Promise<T> =>
  request<T>(path, { method: "POST", body: JSON.stringify(body) });

export const api = {
  me: () => request<MeResponse>(ROUTES.me),
  /** What this deploy has set up; the sign-in screen reads which providers to offer. */
  health: () => request<HealthReport>(ROUTES.health),
  /** `turnstile` is the widget's token, which the worker asks for only of a new address while signup is open. */
  startSignIn: (email: string, turnstile?: string) =>
    post<{ ok: true }>(ROUTES.authStart, { email, ...(turnstile ? { turnstile } : {}) }),
  /** Self-serve signup: a new organisation, the caller its first admin. */
  signup: (name: string, turnstile: string) => post<SignupResponse>(ROUTES.signup, { name, turnstile }),
  confirmSignIn: (token: string) => post<{ ok: true }>(ROUTES.authVerify, { token }),
  signOut: () => post<{ ok: true }>(ROUTES.logout),
  signOutEverywhere: () => post<{ ok: true }>(ROUTES.logoutEverywhere),
  /** How often the signed-in person's notices are mailed, in every organisation. */
  setMailEvery: (every: MailEvery) => post<MailEveryResponse>(ROUTES.meMail, { every }),
  /** Which of the signed-in person's notices are mailed, in every organisation. */
  setMailWhat: (what: MailWhat) => post<MailWhatResponse>(ROUTES.meMailWhat, { what }),
  /** The platform admin console. Every write answers with the whole overview again. */
  admin: {
    overview: () => request<AdminOverview>(ROUTES.admin),
    invite: (email: string, note: string) => post<AdminOverview>(ROUTES.adminAllowlist, { email, note }),
    uninvite: (email: string) =>
      request<AdminOverview>(`${ROUTES.adminAllowlist}/${encodeURIComponent(email)}`, { method: "DELETE" }),
    setStatus: (id: string, status: AccountStatus) =>
      post<AdminOverview>(`${ROUTES.adminAccounts}/${encodeURIComponent(id)}/status`, { status }),
    reseal: () => post<AdminOverview>(ROUTES.adminReseal, {}),
    createOrganization: (name: string, adminEmail: string, plan: string) =>
      post<AdminOverview>(ROUTES.adminOrganizations, { name, adminEmail, plan }),
    close: (id: string) =>
      post<AdminOverview>(`${ROUTES.adminOrganizations}/${encodeURIComponent(id)}/close`, {}),
    reopen: (id: string) =>
      post<AdminOverview>(`${ROUTES.adminOrganizations}/${encodeURIComponent(id)}/reopen`, {}),
    setPlan: (id: string, plan: string) =>
      post<AdminOverview>(`${ROUTES.adminOrganizations}/${encodeURIComponent(id)}/plan`, { plan }),
    renameOrganization: (id: string, name: string) =>
      post<AdminOverview>(`${ROUTES.adminOrganizations}/${encodeURIComponent(id)}/name`, { name }),
    /** One organisation, read-only; the look is recorded in the console's log. */
    viewOrganization: (id: string) =>
      request<AdminOrganizationView>(`${ROUTES.adminOrganizations}/${encodeURIComponent(id)}`),
    setMailAllowance: (id: string, perHour: number | null) =>
      post<AdminOverview>(`${ROUTES.adminOrganizations}/${encodeURIComponent(id)}/mail`, { perHour }),
    decideMailRequest: (id: number, decision: "grant" | "decline") =>
      post<AdminOverview>(`${ROUTES.adminMailRequests}/${id}`, { decision }),
    addPlatformAdmin: (email: string) => post<AdminOverview>(ROUTES.adminPlatformAdmins, { email }),
    removePlatformAdmin: (email: string) =>
      request<AdminOverview>(`${ROUTES.adminPlatformAdmins}/${encodeURIComponent(email)}`, {
        method: "DELETE",
      }),
  },
};

const segment = encodeURIComponent;

/**
 * One organisation's work. Every write answers with what the screen redraws
 * from -- the people, the setup, or the board -- read back from what was
 * stored, never patched in from what was sent.
 */
export function orgApi(orgId: string) {
  const at = (path: string) => orgPath(orgId, path);
  return {
    people: () => request<PeopleResponse>(at(ORG_PATHS.people)),
    addPerson: (person: PersonAddRequest) => post<PeopleResponse>(at(ORG_PATHS.people), person),
    setAway: (id: string, until: string | null) =>
      post<PeopleResponse>(at(`${ORG_PATHS.people}/${segment(id)}${ORG_PATHS.away}`), { until }),
    updatePerson: (id: string, change: PersonUpdateRequest) =>
      post<PersonUpdateResponse>(at(`${ORG_PATHS.people}/${segment(id)}`), change),
    removePerson: (id: string, handTo: string | null) =>
      post<PeopleResponse>(at(`${ORG_PATHS.people}/${segment(id)}${ORG_PATHS.remove}`), { handTo }),

    setup: () => request<SetupResponse>(at(ORG_PATHS.setup)),
    mailUsage: () => request<MailUsage>(at(ORG_PATHS.mail)),
    requestMail: (ask: MailRequestCreate) => post<MailUsage>(at(ORG_PATHS.mailRequest), ask),
    billing: () => request<BillingView>(at(ORG_PATHS.billing)),
    /** A Stripe Checkout page for a paid plan; `then` is where Stripe sends the admin back to. */
    checkout: (plan: string, then: "setup" | "settings" = "settings") =>
      post<BillingRedirect>(at(ORG_PATHS.billingCheckout), { plan, then }),
    billingPortal: () => post<BillingRedirect>(at(ORG_PATHS.billingPortal), {}),
    createLevel: (level: LevelCreateRequest) => post<SetupResponse>(at(ORG_PATHS.levels), level),
    saveLevel: (id: string, level: LevelSaveRequest) =>
      post<SetupResponse>(at(`${ORG_PATHS.levels}/${segment(id)}`), level),
    setStaffing: (staffing: StaffingRequest) => post<SetupResponse>(at(ORG_PATHS.staffing), staffing),
    departments: () => request<DepartmentsResponse>(at(ORG_PATHS.departments)),
    tailor: (tailor: TailorRequest) => post<TailorResponse>(at(ORG_PATHS.tailor), tailor),
    addPeople: (text: string) => post<PeopleBulkResponse>(at(ORG_PATHS.peopleBulk), { text }),
    addDepartment: (name: string, level: string | null = null) =>
      post<DepartmentsResponse>(at(ORG_PATHS.departments), { name, level }),
    setDepartmentUse: (name: string, level: string | null, off: string[]) =>
      post<DepartmentsResponse>(at(ORG_PATHS.departmentUse), { name, level, off }),
    renameDepartment: (from: string, to: string) =>
      post<DepartmentsResponse>(at(ORG_PATHS.departmentRename), { from, to }),
    archiveDepartment: (name: string, archived: boolean) =>
      post<DepartmentsResponse>(at(ORG_PATHS.departmentArchive), { name, archived }),

    instances: () => request<InstancesResponse>(at(ORG_PATHS.instances)),
    week: () => request<WeekResponse>(at(ORG_PATHS.week)),
    overdue: () => request<OverdueResponse>(at(ORG_PATHS.overdue)),
    start: (start: StartRequest) => post<StartResponse>(at(ORG_PATHS.instances), start),
    startCohort: (cohort: CohortRequest) => post<CohortResponse>(at(ORG_PATHS.cohort), cohort),
    /** `card`, from that card's own page, is the one card whose private values come back (and are logged). */
    board: (id: string, card?: string) =>
      request<BoardResponse>(
        at(`${ORG_PATHS.instances}/${segment(id)}${card ? `?card=${segment(card)}` : ""}`),
      ),
    act: (id: string, key: string, action: ActRequest) =>
      post<BoardResponse>(at(`${ORG_PATHS.instances}/${segment(id)}/tasks/${segment(key)}`), action),
    move: (id: string, move: MoveRequest) =>
      post<BoardResponse>(at(`${ORG_PATHS.instances}/${segment(id)}${ORG_PATHS.move}`), move),
    cancel: (id: string, cancel: CancelRequest) =>
      post<BoardResponse>(at(`${ORG_PATHS.instances}/${segment(id)}${ORG_PATHS.cancel}`), cancel),
    audit: (id: string) => request<AuditResponse>(at(`${ORG_PATHS.instances}/${segment(id)}/audit`)),
    auditSearch: (query: AuditQuery, before: number | null) =>
      request<AuditSearchResponse>(at(`${ORG_PATHS.audit}?${auditParams(query, before)}`)),
    auditExport: (query: AuditQuery) => post<AuditExportResponse>(at(ORG_PATHS.auditExport), query),
    dataExport: (what: DataExport, withPrivate = false) =>
      post<DataExportResponse>(at(`${ORG_PATHS.dataExport}/${what}`), withPrivate ? { withPrivate } : {}),
    migrationPlan: (id: string, to: string) =>
      request<MigrationResponse>(
        at(`${ORG_PATHS.instances}/${segment(id)}${ORG_PATHS.migrate}?to=${segment(to)}`),
      ),
    migrate: (id: string, move: MigrateRequest) =>
      post<BoardResponse>(at(`${ORG_PATHS.instances}/${segment(id)}${ORG_PATHS.migrate}`), move),

    templates: () => request<TemplatesResponse>(at(ORG_PATHS.templates)),
    template: (id: string) => request<TemplateResponse>(at(`${ORG_PATHS.templates}/${segment(id)}`)),
    copyTemplate: (from: string) => post<TemplateResponse>(at(ORG_PATHS.templates), { from }),
    saveTemplate: (id: string, baseVersion: number, template: Template) =>
      post<TemplateResponse>(at(`${ORG_PATHS.templates}/${segment(id)}`), { baseVersion, template }),
    exportTemplate: (id: string) =>
      post<DataExportResponse>(at(`${ORG_PATHS.templates}/${segment(id)}${ORG_PATHS.templateExport}`), {}),
    importTemplates: (file: unknown) => post<TemplateImportResponse>(at(ORG_PATHS.templateImport), { file }),
    deleteTemplate: (id: string) =>
      post<{ ok: true }>(at(`${ORG_PATHS.templates}/${segment(id)}${ORG_PATHS.deleteTemplate}`), {}),
    retireTemplate: (id: string, retired: boolean) =>
      post<TemplateResponse>(at(`${ORG_PATHS.templates}/${segment(id)}${ORG_PATHS.retire}`), { retired }),

    checkpoint: (id: string, request: CheckpointRequest) =>
      post<EmployeeResponse>(at(`${ORG_PATHS.employees}/${segment(id)}${ORG_PATHS.checkpoint}`), request),
    erase: (id: string, reference: string) =>
      post<EmployeeResponse>(at(`${ORG_PATHS.employees}/${segment(id)}${ORG_PATHS.erase}`), { reference }),
    employees: () => request<EmployeesResponse>(at(ORG_PATHS.employees)),
    employee: (id: string) => request<EmployeeResponse>(at(`${ORG_PATHS.employees}/${segment(id)}`)),

    queue: () => request<QueueResponse>(at(ORG_PATHS.queue)),
    notices: () => request<NoticesResponse>(at(ORG_PATHS.notices)),
    readNotices: (upTo: number) => post<NoticesResponse>(at(ORG_PATHS.noticesRead), { upTo }),
    rename: (name: string) => post<OrganizationRenameResponse>(at(ORG_PATHS.name), { name }),
    close: (name: string) => post<{ ok: true }>(at(ORG_PATHS.close), { name }),
    reopen: () => post<{ ok: true }>(at(ORG_PATHS.reopen), {}),
  };
}

/** A query as an address's search part, leaving out what is not set. */
function auditParams(query: AuditQuery, before: number | null): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query))
    if (value !== null && value !== "" && !(key === "kind" && value === "all"))
      params.set(key, String(value));
  if (before !== null) params.set("before", String(before));
  return params.toString();
}

export type OrgApi = ReturnType<typeof orgApi>;

/** A refusal's sentence, or the generic one for anything that was not the worker speaking. */
export const sentence = (problem: unknown): string =>
  problem instanceof ApiError ? problem.message : "Something went wrong. Try again.";
